import React from "react"
import { Activity, Users, Settings, SquareKanban, List, CirclePlus } from "lucide-mui"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"
import { usePanelLink } from "@/shared/hooks"
import { useStore } from "@/store"
import logo from "@/shared/logo-minimal.svg"
import { UserAvatar, VenueAvatar } from "@/components/layout/top_dropdown"
import NavIcon from "@/components/layout/nav_icon"
import i18n from "@/i18n"
import { useVenue } from "@/contexts/venue"

const Separator = () => <div className="w-full h-px bg-white opacity-20 my-2" />

type NavIconButtonProps = {
  onClick: () => void
  i18nKey: string
  children: React.ReactNode
  "data-testid"?: string
}

const NavIconButton: React.FC<NavIconButtonProps> = ({ onClick, i18nKey, children, "data-testid": testId }) => {
  const title = i18n.t(i18nKey)
  return (
    <Tooltip title={title} placement="right">
      <IconButton color="inherit" onClick={onClick} aria-label={title} className="opacity-50" data-testid={testId}>
        {children}
      </IconButton>
    </Tooltip>
  )
}

const Sidebar = () => {
  const linkTo = usePanelLink()
  const { setShowCreateReservationDialog } = useStore()
  const { venue } = useVenue()
  const permissions = venue?.permissions

  return (
    <div
      data-testid="panel-sidebar"
      className="w-20 h-screen fixed left-0 top-0 flex flex-col text-white items-center"
      style={{ backgroundColor: "#101010" }}
    >
      <div className="w-20 h-20 flex items-center justify-center mb-6 bg-gray-950">
        <img src={logo} alt="Tabb" style={{ height: 24 }} data-testid="panel-sidebar-logo" />
      </div>
      <div className="flex flex-col items-center justify-center flex-grow gap-4">
        {permissions?.manageReservations && (
          <NavIconButton
            onClick={() => setShowCreateReservationDialog(true)}
            i18nKey="app.main_navigation.create"
            data-testid="panel-sidebar-nav-create"
          >
            <CirclePlus />
          </NavIconButton>
        )}

        {permissions?.manageReservations && <Separator />}

        {permissions?.viewReservations && (
          <NavIcon
            to={linkTo("reservations")}
            end
            i18nKey="app.main_navigation.reservations"
            data-testid="panel-sidebar-nav-reservations"
          >
            <SquareKanban />
          </NavIcon>
        )}

        {permissions?.viewReservations && (
          <NavIcon
            to={linkTo("reservations/all")}
            i18nKey="app.main_navigation.all_reservations"
            data-testid="panel-sidebar-nav-all-reservations"
          >
            <List />
          </NavIcon>
        )}

        {permissions?.viewActivityLog && (
          <NavIcon
            to={linkTo("activity_log")}
            i18nKey="app.main_navigation.activity_log"
            data-testid="panel-sidebar-nav-activity-log"
          >
            <Activity />
          </NavIcon>
        )}

        {permissions?.viewClients && (
          <NavIcon to={linkTo("clients")} i18nKey="app.main_navigation.clients" data-testid="panel-sidebar-nav-clients">
            <Users />
          </NavIcon>
        )}

        <NavIcon
          to={linkTo("settings")}
          i18nKey="app.main_navigation.settings"
          data-testid="panel-sidebar-nav-settings"
        >
          <Settings />
        </NavIcon>
      </div>
      <UserAvatar />
      <VenueAvatar />
    </div>
  )
}

export default Sidebar
